import { useMutation } from '@tanstack/react-query';
import { withNamespaces } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from 'reactstrap';

import DocumentManagementService, {
    getDocumentApiError,
} from 'src/modules/3rd-party-management/apis/admin/DocumentManagementService';

const DeleteDocumentModal = ({
    t,
    isOpen,
    toggle,
    onComplete,
    onDelete,
    onDenied,
    document,
    successMessage,
}) => {
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (onDelete) {
                return await onDelete(document);
            }

            const service = DocumentManagementService.getInstance();

            return await service.delete(document.id);
        },
        onSuccess: () => {
            toast(successMessage || t('Document deleted successfully.'), {
                type: 'success',
            });
            onComplete?.();
            toggle();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const apiError = getDocumentApiError(error);

            toast(apiError.message || t('An error occurred while deleting the document.'), {
                type: 'error',
            });

            if (apiError.status === 403) {
                onDenied?.(document);
            }

            if (apiError.status === 403 || apiError.status === 404) {
                onComplete?.();
                toggle();
            }
        },
    });

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
        >
            <ModalHeader toggle={toggle}>
                {t('Confirm Document Deletion')}
            </ModalHeader>
            <ModalBody>
                {t('Are you sure you want to delete')}{' '}
                <strong>
                    {document?.title
                        || document?.originName
                        || document?.origin_name}
                </strong>
                ?
                <br />
                {t('This action cannot be undone.')}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="danger"
                    type="button"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isLoading}
                >
                    {deleteMutation.isLoading && (
                        <Spinner
                            size="sm"
                            color="white"
                            className="me-1"
                        />
                    )}
                    {t('Delete')}
                </Button>
                <Button
                    color="secondary"
                    type="button"
                    onClick={toggle}
                    disabled={deleteMutation.isLoading}
                >
                    {t('Cancel')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(DeleteDocumentModal);
